import api from './api';

export interface DocumentRecord {
  id?: number;
  user_id?: number;
  nation?: string;
  files?: number[]; // 媒体库ID数组
  name?: string;
  remark?: string;
  upload_at?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  media_files_info?: Array<{
    id?: number;
    name?: string;      // 媒体文件原始名
    file_name?: string; // 兼容字段
    size?: number;      // 媒体文件大小
    file_size?: number; // 兼容字段
    type?: string;      // 文件类型
    file_type?: string; // 兼容字段
    mime_type?: string;
    path?: string;      // 文件路径
    url?: string;       // 直接URL
  }>;
}

export interface DocumentListParams {
  page?: number;
  per_page?: number;
  search?: string;
  nation?: string;
}

export interface DocumentListResponse {
  success: boolean;
  data: {
    data: DocumentRecord[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface DocumentResponse {
  success: boolean;
  message?: string;
  data?: DocumentRecord;
  errors?: any;
}

export interface UploadData {
  nation: string;
  name: string;
  title?: string;
  alt_text?: string;
  description?: string;
}

class DocumentService {
  // 获取文件记录列表
  async getFiles(params?: DocumentListParams): Promise<DocumentListResponse> {
    const response = await api.get('/documents', { params });
    return response.data;
  }

  // 创建文件记录
  async createFile(fileData: DocumentRecord): Promise<DocumentResponse> {
    const response = await api.post('/documents', fileData);
    return response.data;
  }

  // 获取文件记录详情
  async getFile(id: number): Promise<DocumentResponse> {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  }

  // 更新文件记录
  async updateFile(id: number, fileData: DocumentRecord): Promise<DocumentResponse> {
    const response = await api.put(`/documents/${id}`, fileData);
    return response.data;
  }

  // 删除文件记录
  async deleteFile(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  }

  // 批量删除文档
  async batchDeleteDocuments(ids: number[]): Promise<{ success: boolean; message: string; data?: { deleted_count: number } }> {
    const response = await api.post('/documents/batch-delete', { ids });
    return response.data;
  }

  // 导出文档数据
  async exportDocuments(params?: DocumentListParams): Promise<Blob> {
    const response = await api.get('/documents-export', {
      params,
      responseType: 'blob',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
    return response.data;
  }

  // 导入文档数据
  async importDocuments(file: File): Promise<{ success: boolean; message: string; data?: any }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/documents-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 上传文件
  async uploadFile(file: File, data: UploadData): Promise<DocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('nation', data.nation);
    formData.append('name', data.name);
    if (data.title) formData.append('title', data.title);
    if (data.alt_text) formData.append('alt_text', data.alt_text);
    if (data.description) formData.append('description', data.description);

    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }



  // 获取文档列表（用于下拉选择，返回ID和名称）
  async getDocumentsList(search?: string): Promise<{
    success: boolean;
    data: Array<{ value: number; label: string }>;
  }> {
    const params: any = {
      per_page: 100, // 获取更多数据
    };

    if (search) {
      params.search = search;
    }

    const response = await api.get('/documents', { params });

    if (response.data.success) {
      const documents = response.data.data.data;

      const options = documents.map((doc: DocumentRecord) => ({
        value: doc.id!,  // 使用文档ID作为值
        label: doc.name || (doc.media_files_info && doc.media_files_info[0]?.name) || `文档${doc.id}`  // 使用文档名称或首个媒体文件名
      }));

      return {
        success: true,
        data: options
      };
    }

    return {
      success: false,
      data: []
    };
  }

  // 根据文档ID获取文档详情（包含所有相关文件）
  async getDocumentsByIds(documentIds: number[]): Promise<{
    success: boolean;
    data: DocumentRecord[];
  }> {
    try {
      if (documentIds.length === 0) {
        return { success: true, data: [] };
      }

      // 批量获取文档详情
      const promises = documentIds.map(id => this.getFile(id));
      const results = await Promise.all(promises);

      const documents = results
        .filter(result => result.success && result.data)
        .map(result => result.data!);

      return {
        success: true,
        data: documents
      };
    } catch (error) {
      console.error('获取文档详情失败:', error);
      return {
        success: false,
        data: []
      };
    }
  }

  // 根据文件名获取文档详情（包含所有相关文件）
  async getDocumentsByNames(fileNames: string[]): Promise<{
    success: boolean;
    data: Record<string, DocumentRecord[]>;
  }> {
    try {
      const promises = fileNames.map(async (fileName) => {
        const response = await api.get('/documents', {
          params: {
            search: fileName,
            per_page: 50
          }
        });

        if (response.data.success) {
          const allDocs = response.data.data.data || [];

          // 更灵活的匹配逻辑：模糊匹配多个字段
          const matchedDocs = allDocs.filter((doc: DocumentRecord) => {
            const searchTarget = fileName.toLowerCase();
            return (
              // 完全匹配
              doc.name === fileName ||
              // 模糊匹配
              (doc.name && doc.name.toLowerCase().includes(searchTarget)) ||
              // 检查媒体文件名
              (doc.media_files_info && doc.media_files_info.some(media =>
                (media.name && media.name.toLowerCase().includes(searchTarget)) ||
                (media.file_name && media.file_name.toLowerCase().includes(searchTarget))
              ))
            );
          });

          return { fileName, docs: matchedDocs };
        }
        return { fileName, docs: [] };
      });

      const results = await Promise.all(promises);
      const documentMap: Record<string, DocumentRecord[]> = {};

      results.forEach(({ fileName, docs }) => {
        documentMap[fileName] = docs;
      });

      return {
        success: true,
        data: documentMap
      };
    } catch (error) {
      console.error('获取文档详情失败:', error);
      return {
        success: false,
        data: {}
      };
    }
  }
}

const documentService = new DocumentService();
export default documentService;
